import type { LatestReading } from "@spf/contracts";
import type { FreshnessVerdict } from "../lib/useFreshness.js";
import { formatDuration } from "../lib/format.js";

/**
 * The fifth state docs/REQUIREMENTS.md section 4 requires of every telemetry
 * view. A dashboard that silently shows three-day-old numbers as current is a
 * defect, so the age is always stated and lateness is called out.
 *
 * The verdict is handed in rather than worked out here, so that the diagram
 * beside this sentence dims on the same answer. See useFreshness.
 *
 * Restyled in slice 5B, structurally unchanged on purpose. Each branch is a
 * single `<p>` carrying both the words and the live-region role, because one
 * test finds the sentence by its text and then asserts `role="status"` on that
 * same element -- wrapping the words in a styled span would return the span
 * and break it. Returning `null` rather than an empty wrapper matters for the
 * same reason: a test asserts the container is empty when there is no age.
 */
export function Freshness({
  value,
  freshness,
}: {
  value: LatestReading;
  freshness: FreshnessVerdict;
}) {
  if (freshness.ageSeconds === null) return null;

  // Dated ahead of the server, which is not an age at all. The stale rule is a
  // greater-than comparison a negative age can never satisfy, so without this
  // an inverter running an hour fast reads as current for an hour after
  // telemetry actually stops -- and formatDuration takes the magnitude, so it
  // would have said "1 hour ago".
  if (freshness.ageSeconds < 0) {
    return (
      <p className={alertClass} role="status">
        This reading is dated {formatDuration(freshness.ageSeconds)} ahead of the server&rsquo;s
        clock, so how current it is cannot be judged. Check the inverter&rsquo;s clock and the site
        timezone.
      </p>
    );
  }

  if (freshness.stale) {
    return (
      <p className={alertClass} role="status">
        This reading is {formatDuration(freshness.ageSeconds)} old, past the{" "}
        {formatDuration(value.staleAfterSeconds)} freshness limit. Check that the inverter and its
        dongle are powered and online, and that this page still has a connection.
      </p>
    );
  }

  return (
    <p className="text-sm text-ink-muted" role="status">
      Updated {formatDuration(freshness.ageSeconds)} ago.
    </p>
  );
}

const alertClass =
  "rounded-2xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger-ink";
